import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import uuid from 'uuid/v4';
import get from 'lodash/get';
import reduce from 'lodash/reduce';
import noop from 'lodash/noop';
import { Flyout } from '@els/els-react--flyout';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Checkbox } from '@els/els-react--checkbox';
import * as WindowHelper from '@Services/WindowHelper';
import htmlStripsService from '@Services/HtmlStripsService';
import ReactTableColumnSwitcherHelper from '@Components/Results/services/react-table-columns-switcher-helper.service';
import { initHeaders, getNonEmptyColumns, getPossibleToDisplayColumns, getSavedColumns } from './TableColumnsSwitcherHelpers';

const TableColumnsSwitcherComponent = React.forwardRef(({ headers, uniqueId: tableId, containerRef, defaultColumns, updateHeaders, scrollable }, ref) => {
  const [stateHeaders, setStateHeaders] = useState(initHeaders(headers));
  const flyoutRef = useRef(null);
  const generatedId = useRef(uuid());
  const offDynamicHandlersRef = useRef(null);

  function makeColumnsVisible(selectedColumns = []) {
    const columns = ReactTableColumnSwitcherHelper.getTableColumns([...stateHeaders]);

    columns.forEach((header) => {
      // eslint-disable-next-line no-param-reassign
      header.isVisible = false;
      // eslint-disable-next-line no-param-reassign
      header._isChecked = false;
    }); // Hide all
    selectedColumns.forEach((header) => {
      // eslint-disable-next-line no-param-reassign
      header.isVisible = true;
      // eslint-disable-next-line no-param-reassign
      header._isChecked = true;
    }); // Show selected

    setStateHeaders(columns);
    updateHeaders(columns);
  }

  function showDynamicColumns() {
    const columns = getPossibleToDisplayColumns(stateHeaders, defaultColumns, get(containerRef, 'current.offsetWidth'));
    makeColumnsVisible(columns);
  }

  function disableDynamicState() {
    if (offDynamicHandlersRef.current) offDynamicHandlersRef.current();
  }

  function enableDynamicState() {
    disableDynamicState();
    const offResize = WindowHelper.onResize(() => showDynamicColumns());
    offDynamicHandlersRef.current = () => {
      offDynamicHandlersRef.current = null;
      offResize();
    };
  }

  function setSwitcherState(state) {
    disableDynamicState();
    switch (state) {
      case ReactTableColumnSwitcherHelper.DYNAMIC:
        enableDynamicState();
        showDynamicColumns();
        break;
      case ReactTableColumnSwitcherHelper.NONEMPTY:
        makeColumnsVisible(getNonEmptyColumns(stateHeaders));
        break;
      case ReactTableColumnSwitcherHelper.CUSTOM:
        makeColumnsVisible(ReactTableColumnSwitcherHelper.getSelectedColumns(stateHeaders));
        break;
      case ReactTableColumnSwitcherHelper.SAVED:
        getSavedColumns(stateHeaders, tableId)
          .then(
            (columns) => makeColumnsVisible(columns),
            () => setSwitcherState(ReactTableColumnSwitcherHelper.DYNAMIC)
          )
          .catch(Logger.error);
        break;
      default:
        Logger.error(`TableColumnsSwitcherComponent doesn't support '${state}' state.`);
    }
  }

  function showAllNonEmptyCols() {
    setSwitcherState(ReactTableColumnSwitcherHelper.NONEMPTY);
    ReactTableColumnSwitcherHelper.saveColumns(tableId, stateHeaders);
  }

  function resetDefault() {
    setSwitcherState(ReactTableColumnSwitcherHelper.DYNAMIC);
    ReactTableColumnSwitcherHelper.saveColumns(tableId, stateHeaders);
  }

  function applyChanges() {
    setSwitcherState(ReactTableColumnSwitcherHelper.CUSTOM);
    ReactTableColumnSwitcherHelper.saveColumns(tableId, stateHeaders);
  }

  function toggleCheckbox(event, id) {
    const value = event?.currentTarget?.checked;
    const _headers = [...stateHeaders];
    const _header = _headers.find((_header, index) => `rx-checkbox-${_header.id}-${generatedId.current}--${index}` === id)
    _header._isChecked = value;
    setStateHeaders(_headers);
  }

  function isLastChecked({ _isChecked }) {
    return (
      _isChecked &&
      reduce(ReactTableColumnSwitcherHelper.getTableColumns(stateHeaders), (memo, headerVal) => memo + !!headerVal._isChecked, 0) <= 1
    );
  }

  React.useImperativeHandle(ref, () => ({
    showAllNonEmptyCols,
  }));

  useEffect(() => {
    setSwitcherState(ReactTableColumnSwitcherHelper.SAVED);
    return () => disableDynamicState();
  }, []);

  return (
    <Flyout
      className="table-column-switcher"
      ref={flyoutRef}
      flyout={
        <div className="table-column-switcher__content">
          <h4 className="u-els-font-size-h4">Show/Hide columns</h4>
          <div className={cn('u-els-margin-top-1x1o2', { scrollable })}>
            {stateHeaders.map((header, index) => {
              const id = `rx-checkbox-${header.id}-${generatedId.current}--${index}`;
              return (
                <Checkbox
                  key={id}
                  id={id}
                  checked={header._isChecked}
                  onChange={(event) => toggleCheckbox(event, id)}
                  label={htmlStripsService.unicodeHtmlEntities(header.title)}
                  title={`Click for show/hide ${htmlStripsService.getTitleWithoutHtml(header.title, 'full')}`}
                  className="u-els-margin-bottom"
                  disabled={isLastChecked(header)}
                />
              );
            })}
          </div>
          <div className="table-column-switcher__buttons u-els-margin-top-1x1o2">
            <Button
              type="primary"
              className="e2e-column-switcher-apply"
              onClick={() => {
                applyChanges();
                flyoutRef.current?.toggleVisibility(false);
              }}>
              Apply
            </Button>
            <Button
              type="secondary"
              className="e2e-column-switcher-reset"
              onClick={() => {
                resetDefault();
                flyoutRef.current?.toggleVisibility(false);
              }}>
              Reset
            </Button>
          </div>
        </div>
      }
      shouldCloseOnESC
      showCloseButton
      placement="bottom-end"
      pointerType="primary">
      <ButtonWithIcon sprite="ChevronDown" type="link" iconRight className="e2e-show-hide-columns" onClick={noop}>
        Show/Hide columns
      </ButtonWithIcon>
    </Flyout>
  );
});

export default TableColumnsSwitcherComponent;
